// =============================================================================
// GALLERY SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { GalleryComponent } from '@/types/components';

interface GallerySectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new GalleryComponent format
 */
function isNewGalleryFormat(component: ContentComponent): boolean {
  return (
    component.type === 'gallery' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Gallery Section Component
 */
export const GallerySection: React.FC<GallerySectionProps> = ({ component }) => {
  if (isNewGalleryFormat(component)) {
    return <GallerySectionNew component={component as unknown as any} />;
  }
  return <GallerySectionLegacy component={component} />;
};

/**
 * New format Gallery Section
 */
const GallerySectionNew: React.FC<{ component: GalleryComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % items.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + items.length) % items.length);
  };
  
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    
    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [lightboxOpen, items.length]);
  
  return (
    <>
      <section id={id} className={`content-section gallery-section ${layoutClasses}`} style={displayStyles}>
        <div className="container">
          {content.title && (
            <div className="section-header">
              <h2>{content.title}</h2>
              {content.description && <p>{content.description}</p>}
            </div>
          )}
          
          <div className={`gallery-grid grid-cols-${layout.columns || 3}`}>
            {items.map((item, index) => (
              <div key={item.id || index} className="gallery-item" onClick={() => openLightbox(index)}>
                <div className="gallery-image">
                  <img src={item.image} alt={item.alt || item.title} loading="lazy" />
                  <div className="gallery-overlay">
                    <ZoomIn size={24} className="zoom-icon" />
                    {item.title && (
                      <div className="gallery-info">
                        <h4>{item.title}</h4>
                        {item.description && <p>{item.description}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {lightboxOpen && items[currentImageIndex] && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <X size={24} />
            </button>
            
            <button className="lightbox-prev" onClick={prevImage} aria-label="Previous image">
              <ChevronLeft size={24} />
            </button>
            
            <button className="lightbox-next" onClick={nextImage} aria-label="Next image">
              <ChevronRight size={24} />
            </button>
            
            <div className="lightbox-image">
              <img
                src={items[currentImageIndex].image}
                alt={items[currentImageIndex].alt || items[currentImageIndex].title}
              />
            </div>
            
            <div className="lightbox-info">
              {items[currentImageIndex].title && (
                <h4>{items[currentImageIndex].title}</h4>
              )}
              {items[currentImageIndex].description && (
                <p>{items[currentImageIndex].description}</p>
              )}
            </div>
            
            <div className="lightbox-counter">
              {currentImageIndex + 1} / {items.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Legacy format Gallery Section
 */
const GallerySectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const gallery = metadata?.gallery || [];
  const columns = metadata?.columns || 3;
  
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };
  
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    
    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [lightboxOpen, gallery.length]);
  
  return (
    <>
      <section id={id} className="content-section gallery-section">
        <div className="container">
          {title && (
            <div className="section-header">
              <h2>{title}</h2>
              {content && <p>{content}</p>}
            </div>
          )}
          
          <div className={`gallery-grid grid-cols-${columns}`}>
            {gallery.map((item: any, index: number) => (
              <div key={index} className="gallery-item" onClick={() => openLightbox(index)}>
                <div className="gallery-image">
                  <img src={item.image} alt={item.alt} loading="lazy" />
                  <div className="gallery-overlay">
                    <ZoomIn size={24} className="zoom-icon" />
                    {item.title && (
                      <div className="gallery-info">
                        <h4>{item.title}</h4>
                        {item.description && <p>{item.description}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {lightboxOpen && gallery[currentImageIndex] && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <X size={24} />
            </button>
            
            <button className="lightbox-prev" onClick={prevImage} aria-label="Previous image">
              <ChevronLeft size={24} />
            </button>
            
            <button className="lightbox-next" onClick={nextImage} aria-label="Next image">
              <ChevronRight size={24} />
            </button>
            
            <div className="lightbox-image">
              <img
                src={gallery[currentImageIndex].image}
                alt={gallery[currentImageIndex].alt}
              />
            </div>
            
            <div className="lightbox-info">
              {gallery[currentImageIndex].title && (
                <h4>{gallery[currentImageIndex].title}</h4>
              )}
              {gallery[currentImageIndex].description && (
                <p>{gallery[currentImageIndex].description}</p>
              )}
            </div>
            
            <div className="lightbox-counter">
              {currentImageIndex + 1} / {gallery.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { GallerySectionNew, GallerySectionLegacy };

